import React from 'react'
import { Box, Tooltip } from '@mui/material'
import { AGE, C, TONE, tnum } from './tokens'
import { dueAt, isActive, isInTransit, isStale, relative, smartTime, timeShort } from './model'

const clamp = t => Math.min(1, Math.max(0, t))

function nodesFor(v) {
  if (!v.pickup && !v.dropoff) return null
  const nodes = [{ key: 'placed', label: 'Placed', at: v.placedAt, done: true }]
  if (v.pickup) nodes.push({ key: 'pickup', label: v.type === 'PICKUP' ? 'Ready' : 'Pickup', at: v.pickup.at, done: v.pickup.done })
  if (v.dropoff) nodes.push({ key: 'dropoff', label: 'Dropoff', at: v.dropoff.at, done: v.dropoff.done })
  return nodes.length > 1 ? nodes : null
}

/** Share of the track to fill: each gap between nodes is an equal slice, filled by elapsed time. */
function progress(v, nodes, now) {
  if (v.status === 'DELIVERED' || v.status === 'COLLECTED') return 1
  if (v.status === 'CANCELLED') return 0
  const slices = nodes.length - 1
  let p = 0
  for (let i = 0; i < slices; i++) {
    const a = nodes[i]
    const b = nodes[i + 1]
    p += clamp((now - a.at) / Math.max(1, b.at - a.at)) / slices
    // Don't run past a step that hasn't actually happened.
    if (!b.done) break
  }
  return p
}

/** Placed → pickup → dropoff on one line: solid = happened, hollow = estimate, red = past due. */
export default function EtaTimeline({ view, now, compact }) {
  const nodes = nodesFor(view)
  if (!nodes) return <Box component="span" sx={{ fontSize: 12, color: C.muted }}>No ETA</Box>

  const stale = isStale(view, now)
  const late = isActive(view) && !stale && view.status !== 'SCHEDULED' && now > dueAt(view)
  const muted = stale || view.status === 'CANCELLED'
  const p = progress(view, nodes, now)
  const last = nodes[nodes.length - 1]
  const fill = muted ? AGE.stale.bar : late ? AGE.late.bar : isInTransit(view) ? TONE.transit.dot : 'rgba(16,24,40,0.55)'

  const track = (
    <Box sx={{ position: 'relative', height: compact ? 10 : 12, width: compact ? 96 : '100%', flexShrink: 0 }}>
      <Box sx={{ position: 'absolute', left: 4, right: 4, top: '50%', borderTop: `1px dashed ${C.input}` }} />
      <Box
        sx={{
          position: 'absolute', left: 4, top: '50%', height: 2, mt: '-1px', borderRadius: 1, bgcolor: fill,
          width: `calc(${p * 100}% - ${p * 8}px)`, transition: 'width 700ms ease',
        }}
      />
      {nodes.map((n, i) => {
        const pos = i / (nodes.length - 1)
        const lateNode = late && i === nodes.length - 1
        return (
          <Box
            key={n.key}
            component="span"
            sx={{
              position: 'absolute', top: '50%', width: 8, height: 8, borderRadius: '50%',
              left: `calc(4px + ${pos} * (100% - 8px))`, transform: 'translate(-50%, -50%)',
              bgcolor: lateNode ? AGE.late.bg : n.done ? (muted ? AGE.stale.bar : 'rgba(16,24,40,0.7)') : C.card,
              boxShadow: lateNode ? `inset 0 0 0 1.5px ${AGE.late.bar}` : n.done ? 'none' : `inset 0 0 0 1.5px ${C.input}`,
            }}
          />
        )
      })}
    </Box>
  )

  return (
    <Tooltip
      arrow
      placement="bottom"
      title={
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, py: 0.25, ...tnum }}>
          {nodes.map(n => (
            <Box key={n.key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 3 }}>
              <span style={{ opacity: 0.75 }}>{n.label}{!n.done && ' (est.)'}</span>
              <span>
                {timeShort(n.at, view.restaurant?.timezone)}
                {!n.done && !muted && <span style={{ opacity: 0.75 }}> · {relative(n.at, now)}</span>}
              </span>
            </Box>
          ))}
          {late && <span style={{ fontWeight: 600 }}>Past due by {relative(dueAt(view), now).replace(' ago', '')}</span>}
        </Box>
      }
    >
      <Box sx={compact ? { display: 'flex', alignItems: 'center', gap: 1, cursor: 'default' } : { width: 152, cursor: 'default' }}>
        {track}
        {compact ? (
          <Box component="span" sx={{ fontSize: 12, whiteSpace: 'nowrap', ...tnum, color: late ? AGE.late.ink : last.done ? C.text : C.muted, fontWeight: late ? 600 : 400 }}>
            {smartTime(last.at, now, view.restaurant?.timezone)}
          </Box>
        ) : (
          <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'space-between', gap: 0.5, fontSize: 11, lineHeight: 1, whiteSpace: 'nowrap', ...tnum }}>
            {nodes.map((n, i) => {
              const isLast = i === nodes.length - 1
              return (
                <Box
                  component="span"
                  key={n.key}
                  sx={{
                    color: muted ? C.muted : isLast && late ? AGE.late.ink : n.done ? 'rgba(16,24,40,0.85)' : C.muted,
                    fontWeight: isLast && late ? 600 : 400,
                  }}
                >
                  {smartTime(n.at, now, view.restaurant?.timezone)}
                </Box>
              )
            })}
          </Box>
        )}
      </Box>
    </Tooltip>
  )
}
